import { ChartSkeleton } from 'components/Analytics/Skeletons'
import type { PoolLiquidityChartPoint } from 'hooks/analytics/useV3PoolLiquidityChart'
import { useV3PoolLiquidityChart } from 'hooks/analytics/useV3PoolLiquidityChart'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis } from 'recharts'
import { formatAmount } from 'utils/formatDollarAmt'

import ZoomControls from './ZoomControls'

const BAR_COLOR = '#2172E5'
const BAR_ACTIVE_COLOR = '#FB118E'
const TOOLTIP_BACKGROUND = 'rgba(13,24,45,0.92)'
const TOOLTIP_BORDER = 'rgba(64,81,137,0.45)'

const INITIAL_WINDOW = 401
const MIN_WINDOW = 41
const ZOOM_STEP = 20
const PAN_STEP = 40

interface TokenDisplay {
  address?: string
  symbol?: string
  name?: string
  decimals?: number
}

interface PoolLiquidityChartProps {
  token0: TokenDisplay
  token1: TokenDisplay
  feeTier?: number | null
  currentPrice?: number
  enabled?: boolean
}

interface TooltipProps {
  active?: boolean
  payload?: Array<{ payload: PoolLiquidityChartPoint }>
  token0Symbol?: string
  token1Symbol?: string
  currentPriceToken0?: number
}

const formatTickPrice = (value?: number) => {
  if (!Number.isFinite(value)) return '-'
  return Number(value).toLocaleString('en-US', { minimumSignificantDigits: 1, maximumSignificantDigits: 8 })
}

const formatLockedAmount = (value?: number) => {
  if (!Number.isFinite(value)) return '-'
  const abs = Math.abs(value as number)
  if (abs === 0) return '0'
  return formatAmount(abs)
}

const LiquidityTooltip = ({ active, payload, token0Symbol, token1Symbol, currentPriceToken0 }: TooltipProps) => {
  if (!active || !payload?.length) return null
  const entry = payload[0]?.payload
  if (!entry) return null

  const hasCurrentPrice = typeof currentPriceToken0 === 'number' && Number.isFinite(currentPriceToken0)
  const hasTickPrice0 = Number.isFinite(entry.price0)
  const hasTickPrice1 = Number.isFinite(entry.price1)

  const currentPriceValue = hasCurrentPrice ? (currentPriceToken0 as number) : undefined
  const tickPrice1Value = hasTickPrice1 ? entry.price1 : undefined

  const showToken0Locked =
    currentPriceValue !== undefined && hasTickPrice0 && tickPrice1Value !== undefined
      ? currentPriceValue > tickPrice1Value
      : false

  const lockedValue = showToken0Locked ? entry.tvlToken0 : entry.tvlToken1
  const lockedFormatted = formatLockedAmount(lockedValue)
  const lockedSymbol = showToken0Locked ? token0Symbol : token1Symbol

  return (
    <div
      className="min-w-[248px] rounded-lg border text-sm text-white shadow-2xl"
      style={{ background: TOOLTIP_BACKGROUND, borderColor: TOOLTIP_BORDER }}
    >
      <div className="px-4 py-3 font-mono text-[13px] leading-relaxed">
        <div className="mb-2 text-[11px] uppercase font-bold tracking-[0.12em] text-gray-400">Tick Stats</div>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-6">
            <span>{token0Symbol ?? 'Token 0'} Price:</span>
            <span>
              {formatTickPrice(entry.price0)} {token1Symbol ?? ''}
            </span>
          </div>
          <div className="flex items-center justify-between gap-6">
            <span>{token1Symbol ?? 'Token 1'} Price:</span>
            <span>
              {formatTickPrice(entry.price1)} {token0Symbol ?? ''}
            </span>
          </div>
          {lockedSymbol ? (
            <div className="flex items-center justify-between gap-6">
              <span>{lockedSymbol} Locked:</span>
              <span>
                {lockedFormatted} {lockedSymbol}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

const CurrentPriceCard = ({
  token0Symbol,
  token1Symbol,
  price0,
  price1,
}: {
  token0Symbol?: string
  token1Symbol?: string
  price0?: number
  price1?: number
}) => {
  if (!Number.isFinite(price0) || !Number.isFinite(price1)) return null

  return (
    <div
      className="pointer-events-none rounded-lg border border-[#1F2F4C] bg-kodiak-card-primary px-4 py-3 text-xs text-white shadow-[0_10px_24px_rgba(10,19,34,0.45)]"
      role="note"
    >
      <div className="mb-2 flex items-center gap-2 font-bold uppercase tracking-[0.1em] text-gray-400">
        Current Price
        <span className="inline-flex h-2 w-2 rounded-full" style={{ backgroundColor: BAR_ACTIVE_COLOR }} />
      </div>
      <div className="space-y-1 font-mono text-[13px] leading-relaxed">
        <div>
          1 {token0Symbol ?? 'Token 0'} = {formatTickPrice(price0)} {token1Symbol ?? ''}
        </div>
        <div>
          1 {token1Symbol ?? 'Token 1'} = {formatTickPrice(price1)} {token0Symbol ?? ''}
        </div>
      </div>
    </div>
  )
}

const CustomBar = ({
  x,
  y,
  width,
  height,
  fill,
}: {
  x: number
  y: number
  width: number
  height: number
  fill: string
}) => {
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(width) || !Number.isFinite(height)) return null
  return <rect x={x} y={y} width={width} height={height} rx={2} fill={fill} />
}

export default function PoolLiquidityChart({
  token0,
  token1,
  feeTier,
  currentPrice,
  enabled = true,
}: PoolLiquidityChartProps) {
  const { points, currentPoint, isLoading, isUninitialized, canExtendRange, extendRange } = useV3PoolLiquidityChart({
    token0,
    token1,
    feeTier,
    enabled,
  })

  const extendRequestRef = useRef<{ targetWindow: number } | null>(null)
  const [visibleRange, setVisibleRange] = useState<{ start: number; end: number }>({ start: 0, end: -1 })
  const [initialized, setInitialized] = useState(false)
  const [extendPending, setExtendPending] = useState(false)

  useEffect(() => {
    setInitialized(false)
    setVisibleRange({ start: 0, end: -1 })
    extendRequestRef.current = null
    setExtendPending(false)
  }, [token0.address, token1.address, feeTier, enabled])

  const totalCount = points?.length ?? 0
  const activeIndex = useMemo(() => (points ? points.findIndex((point) => point.isCurrent) : -1), [points])

  useEffect(() => {
    if (!points?.length) {
      if (extendPending) {
        setExtendPending(false)
      }
      return
    }

    const total = points.length
    const maxIndex = total - 1
    const nextActiveIndex = activeIndex >= 0 ? activeIndex : null

    if (!initialized) {
      const initialWindow = Math.min(total, INITIAL_WINDOW)
      const center = nextActiveIndex ?? Math.floor(total / 2)
      const maxOffset = Math.max(0, total - initialWindow)
      const start = Math.max(0, Math.min(center - Math.floor(initialWindow / 2), maxOffset))
      const end = Math.min(maxIndex, start + initialWindow - 1)
      setVisibleRange({ start, end })
      setInitialized(true)
      extendRequestRef.current = null
      return
    }

    let start = visibleRange.start
    let end = visibleRange.end

    if (end < start) {
      start = Math.max(0, Math.min(start, maxIndex))
      end = Math.min(maxIndex, start)
    }

    const extendRequest = extendRequestRef.current

    if (extendRequest) {
      if (total >= extendRequest.targetWindow) {
        start = 0
        end = maxIndex
        extendRequestRef.current = null
        if (extendPending) {
          setExtendPending(false)
        }
      } else {
        start = Math.max(0, Math.min(start, maxIndex))
        end = Math.max(start, Math.min(end, maxIndex))
      }
    } else {
      start = Math.max(0, Math.min(start, maxIndex))
      end = Math.max(start, Math.min(end, maxIndex))
      if (extendPending && !isLoading) {
        setExtendPending(false)
      }
    }

    if (start !== visibleRange.start || end !== visibleRange.end) {
      setVisibleRange({ start, end })
    }
  }, [points, activeIndex, initialized, visibleRange.start, visibleRange.end, extendPending, isLoading])

  const handleExtendRange = useCallback(
    ({ targetWindow }: { targetWindow: number }) => {
      if (!canExtendRange) return
      extendRequestRef.current = extendRequestRef.current
        ? { targetWindow: Math.max(extendRequestRef.current.targetWindow, targetWindow) }
        : { targetWindow }
      setExtendPending(true)
      extendRange()
    },
    [canExtendRange, extendRange]
  )

  const zoomedData = useMemo(() => {
    if (!points?.length) return []
    if (visibleRange.end < visibleRange.start) return []
    const start = Math.max(0, Math.min(visibleRange.start, points.length - 1))
    const end = Math.max(start, Math.min(visibleRange.end, points.length - 1))
    return points.slice(start, end + 1)
  }, [points, visibleRange.end, visibleRange.start])

  const price0Value = useMemo(() => {
    if (Number.isFinite(currentPrice) && (currentPrice as number) > 0) return currentPrice as number
    if (Number.isFinite(currentPoint?.price0) && (currentPoint?.price0 ?? 0) > 0) return currentPoint?.price0
    if (Number.isFinite(currentPoint?.price1) && (currentPoint?.price1 ?? 0) > 0) return 1 / (currentPoint?.price1 ?? 1)
    return undefined
  }, [currentPrice, currentPoint])

  const price1Value = useMemo(() => {
    if (Number.isFinite(currentPoint?.price1) && (currentPoint?.price1 ?? 0) > 0) return currentPoint?.price1
    if (Number.isFinite(price0Value) && (price0Value as number) > 0) {
      return 1 / (price0Value as number)
    }
    return undefined
  }, [currentPoint, price0Value])

  const comparisonPrice0 = useMemo(() => {
    if (typeof price0Value === 'number' && Number.isFinite(price0Value) && price0Value > 0) return price0Value
    if (typeof price1Value === 'number' && Number.isFinite(price1Value) && price1Value > 0) {
      const inverted = 1 / price1Value
      return Number.isFinite(inverted) && inverted > 0 ? inverted : undefined
    }
    return undefined
  }, [price0Value, price1Value])

  const displayPrice0 = useMemo(() => {
    const p0 = Number.isFinite(price0Value) && (price0Value as number) > 0 ? (price0Value as number) : undefined
    const p1 = Number.isFinite(price1Value) && (price1Value as number) > 0 ? (price1Value as number) : undefined
    if (p0 !== undefined) {
      if (p0 >= 1) return p0
      if (p1 !== undefined && p1 > 0) return 1 / p1
      return p0
    }
    if (p1 !== undefined && p1 > 0) {
      return 1 / p1
    }
    return undefined
  }, [price0Value, price1Value])

  const displayPrice1 = useMemo(() => {
    const p1 = Number.isFinite(price1Value) && (price1Value as number) > 0 ? (price1Value as number) : undefined
    const p0 = Number.isFinite(price0Value) && (price0Value as number) > 0 ? (price0Value as number) : undefined
    if (p1 !== undefined) {
      if (p1 <= 1) return p1
      if (p0 !== undefined && p0 > 0) return 1 / p0
      return p1
    }
    if (p0 !== undefined && p0 > 0) {
      return 1 / p0
    }
    return undefined
  }, [price0Value, price1Value])

  const windowLength = useMemo(() => {
    if (visibleRange.end < visibleRange.start) return 0
    return visibleRange.end - visibleRange.start + 1
  }, [visibleRange.end, visibleRange.start])

  const minWindow = useMemo(() => Math.max(1, Math.min(MIN_WINDOW, totalCount)), [totalCount])
  const zoomRatio = windowLength > 0 ? Math.max(ZOOM_STEP / windowLength, 1 / windowLength) : 0.05
  const panRatio = windowLength > 0 ? Math.max(PAN_STEP / windowLength, 1 / windowLength) : 0.1
  const showExtendSkeleton = extendPending && isLoading && Boolean(points?.length)

  if ((isLoading || isUninitialized) && !points?.length) {
    return <ChartSkeleton className="min-w-0 min-h-[320px] lg:min-h-[428px]" />
  }

  if (!points?.length) {
    return (
      <div className="flex min-h-[320px] flex-col items-center justify-center gap-2 rounded-2xl border border-gray-700 bg-gray-900 px-6 py-12 text-center lg:min-h-[428px]">
        <div className="text-base font-medium text-white">No liquidity data</div>
        <div className="text-sm text-gray-400">Liquidity distribution is unavailable for this pool right now.</div>
      </div>
    )
  }

  return (
    <div className="flex h-[400px] w-full min-w-0 flex-col rounded-lg border border-gray-700 bg-gray-900 p-4 lg:h-[428px]">
      <div className="mb-3 flex justify-end gap-4">
        {totalCount > 0 ? (
          <ZoomControls
            totalCount={totalCount}
            visibleRange={visibleRange}
            setVisibleRange={setVisibleRange}
            minWindow={minWindow}
            zoomRatio={zoomRatio}
            panRatio={panRatio}
            onRequestExtend={canExtendRange ? handleExtendRange : undefined}
            zoomOutPending={extendPending}
            invertPan
          />
        ) : null}
      </div>

      <div className="relative flex-1">
        {showExtendSkeleton ? (
          <div className="h-full w-full min-h-[260px] animate-pulse rounded-md bg-gray-800/40" />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={zoomedData} margin={{ top: 8, right: 32, left: 24, bottom: 8 }}>
              <Tooltip
                cursor={{ fill: 'rgba(33, 114, 229, 0.12)' }}
                content={(props) => (
                  <LiquidityTooltip
                    {...props}
                    token0Symbol={token0.symbol}
                    token1Symbol={token1.symbol}
                    currentPriceToken0={comparisonPrice0}
                  />
                )}
              />
              <XAxis dataKey="index" type="number" domain={['dataMin', 'dataMax']} hide reversed />
              <Bar dataKey="activeLiquidity" isAnimationActive={false} shape={CustomBar}>
                {zoomedData.map((entry, index) => (
                  <Cell key={`${entry.tick}-${index}`} fill={entry.isCurrent ? BAR_ACTIVE_COLOR : BAR_COLOR} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="flex justify-center">
        <CurrentPriceCard
          token0Symbol={token0.symbol}
          token1Symbol={token1.symbol}
          price0={displayPrice0}
          price1={displayPrice1}
        />
      </div>
    </div>
  )
}
